import React, { useMemo, useState } from "react";
import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import Swal from "sweetalert2";

const REPORTS = [
    {
        key: "batch-traceability",
        title: "Batch Traceability",
        description:
            "Batch-wise utilization across sales orders, work orders, daily plans, POs, requisitions, and returns for the selected date range.",
        path: "/api/reports/batch-traceability/",
    },
    {
        key: "rm-stock-movement",
        title: "RM Stock Movement Summary",
        description:
            "Raw material item-wise consumed, ordered, received, and required-to-order quantities for the selected date range.",
        path: "/api/reports/rm-stock-movement/",
    },
    {
        key: "production-so-completion",
        title: "Production & SO Completion",
        description:
            "Total factory production and percentage of sales orders fully dispatched for the selected date range.",
        path: "/api/reports/production-so-completion/",
    },
    {
        key: "machine-production-variance",
        title: "Machine Production Variance",
        description:
            "Per daily plan: planned vs manual vs IoT recorded quantity (cycles × mould) and cycle count, with differences and % variance.",
        path: "/api/reports/machine-production-variance/",
    },
    {
        key: "client-production",
        title: "Client Production Report",
        description:
            "Department-wise RM/production/energy summary, SO WIP day matrix, and machine day/night drill-down with hyperlinks (XLSX only).",
        path: "/api/reports/client-production/",
        formats: ["xlsx"],
    },
];

const toIsoDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const defaultFromDate = () => {
    const now = new Date();
    return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

const defaultToDate = () => toIsoDate(new Date());

const formatDisplayDate = (isoDate) => {
    if (!isoDate) {
        return "";
    }
    const [year, month, day] = isoDate.split("-").map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const Reports = () => {
    const { apiClient } = useGlobalAPIQueryContext();
    const [fromDate, setFromDate] = useState(defaultFromDate);
    const [toDate, setToDate] = useState(defaultToDate);
    const [loadingKey, setLoadingKey] = useState(null);

    const periodLabel = useMemo(() => {
        if (!fromDate || !toDate) {
            return "";
        }
        return `${formatDisplayDate(fromDate)} – ${formatDisplayDate(toDate)}`;
    }, [fromDate, toDate]);

    const downloadReport = async (report, format) => {
        if (!fromDate || !toDate) {
            Swal.fire({
                title: "Date range required",
                text: "Please select both From and To dates.",
                icon: "warning",
            });
            return;
        }
        if (fromDate > toDate) {
            Swal.fire({
                title: "Invalid date range",
                text: "From date must be on or before To date.",
                icon: "warning",
            });
            return;
        }

        const loadingId = `${report.key}:${format}`;
        try {
            setLoadingKey(loadingId);
            const response = await apiClient.get(report.path, {
                params: {
                    export_format: format,
                    from: fromDate,
                    to: toDate,
                },
                responseType: "blob",
            });
            const blob = response.data;
            const contentDisposition = response.headers["content-disposition"];
            const fallback = `${report.key}.${format === "pdf" ? "pdf" : "xlsx"}`;
            const filename =
                contentDisposition
                    ?.split("filename=")?.[1]
                    ?.replaceAll('"', "")
                    ?.trim() || fallback;

            const href = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = href;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(href);
        } catch (error) {
            let message =
                error?.response?.data?.results?.message ||
                error.message ||
                "Unable to download report";
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const text = await data.text();
                    const parsed = JSON.parse(text);
                    message =
                        parsed?.results?.message ||
                        parsed?.detail ||
                        message;
                } catch (_) {
                    // keep fallback message
                }
            }
            Swal.fire({
                title: "Download failed",
                text: message,
                icon: "error",
            });
        } finally {
            setLoadingKey(null);
        }
    };

    return (
        <div className="nk-content">
            <div className="container-fluid">
                <div className="nk-content-inner">
                    <div className="nk-content-body">
                        <div className="nk-block-head nk-block-head-sm">
                            <div className="nk-block-between">
                                <div className="nk-block-head-content">
                                    <h3 className="nk-block-title page-title">
                                        Reports
                                    </h3>
                                    <div className="nk-block-des text-soft">
                                        <p>
                                            Operational reports for the selected
                                            date range
                                            {periodLabel
                                                ? ` (${periodLabel})`
                                                : ""}
                                            . Download as PDF or Excel.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="nk-block">
                            <div className="card card-bordered mb-4">
                                <div className="card-inner">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-6 col-md-4 col-xl-2">
                                            <div className="form-group mb-0">
                                                <label
                                                    className="form-label"
                                                    htmlFor="reports-from-date"
                                                >
                                                    From
                                                </label>
                                                <input
                                                    id="reports-from-date"
                                                    type="date"
                                                    className="form-control"
                                                    value={fromDate}
                                                    max={toDate || undefined}
                                                    onChange={(event) =>
                                                        setFromDate(
                                                            event.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-4 col-xl-2">
                                            <div className="form-group mb-0">
                                                <label
                                                    className="form-label"
                                                    htmlFor="reports-to-date"
                                                >
                                                    To
                                                </label>
                                                <input
                                                    id="reports-to-date"
                                                    type="date"
                                                    className="form-control"
                                                    value={toDate}
                                                    min={fromDate || undefined}
                                                    onChange={(event) =>
                                                        setToDate(
                                                            event.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-gs">
                                {REPORTS.map((report) => (
                                    <div
                                        className="col-md-12"
                                        key={report.key}
                                    >
                                        <div className="card card-bordered">
                                            <div className="card-inner">
                                                <div className="d-flex justify-between align-center flex-wrap g-2">
                                                    <div>
                                                        <h5 className="card-title">
                                                            {report.title}
                                                        </h5>
                                                        <p className="text-soft mb-0">
                                                            {
                                                                report.description
                                                            }
                                                        </p>
                                                    </div>
                                                    <div className="d-flex g-2">
                                                        {(report.formats || [
                                                            "pdf",
                                                            "xlsx",
                                                        ]).includes("pdf") && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                disabled={
                                                                    loadingKey ===
                                                                    `${report.key}:pdf`
                                                                }
                                                                onClick={() =>
                                                                    downloadReport(
                                                                        report,
                                                                        "pdf"
                                                                    )
                                                                }
                                                            >
                                                                {loadingKey ===
                                                                `${report.key}:pdf`
                                                                    ? "Preparing…"
                                                                    : "Download PDF"}
                                                            </button>
                                                        )}
                                                        {(report.formats || [
                                                            "pdf",
                                                            "xlsx",
                                                        ]).includes("xlsx") && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                disabled={
                                                                    loadingKey ===
                                                                    `${report.key}:xlsx`
                                                                }
                                                                onClick={() =>
                                                                    downloadReport(
                                                                        report,
                                                                        "xlsx"
                                                                    )
                                                                }
                                                            >
                                                                {loadingKey ===
                                                                `${report.key}:xlsx`
                                                                    ? "Preparing…"
                                                                    : "Download XLSX"}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reports;
